import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { fetchRosterClassification } from '../Roster/rosterClassification';
import { shouldSimulateLoad } from './loading';
import { shouldActivateRow } from './rowActivation';
import { formatLastUpdated, usePlansStatus } from './plansGenerated';
import {
  classificationPlanRows,
  formatGeneratedOn,
  type PlanRow,
} from './planRows';
import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { PlansEmptyState } from './PlansEmptyState';
import { planPath } from '../IndividualPlan/planRoute';
import { PLANS } from './tokens';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type LocationState = { count?: number; skipLoad?: boolean } | null;

type Column = {
  key: keyof PlanRow & string;
  label: string;
  width?: number;
};

const COLUMNS: readonly Column[] = [
  { key: 'name', label: 'Student Name' },
  { key: 'school', label: 'School', width: 184 },
  { key: 'elTeacher', label: 'EL Teacher', width: 184 },
  { key: 'grade', label: 'Grade', width: 80 },
  { key: 'accessComposite', label: 'ACCESS Composite', width: 176 },
  { key: 'generatedOn', label: 'Generated On', width: 128 },
];

const ROWS_PER_PAGE = 10;

const SIMULATED_LOAD_MS = 2000;

const HEADER_TYPO = {
  fontFamily: PLANS.font,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '24px',
  color: PLANS.ink,
} as const;

const CELL_TYPO = {
  fontFamily: PLANS.font,
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '24px',
  color: PLANS.ink,
} as const;

function cellSx(width?: number) {
  return {
    ...(width ? { width, flex: 'none' } : { flex: 1 }),
    p: width ? '8px 16px' : '8px 16px 8px 12px',
    borderBottom: `1px solid ${PLANS.border}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const;
}

function HeaderRow() {
  return (
    <Box sx={{ display: 'flex' }}>
      {COLUMNS.map((c) => (
        <Box
          key={c.key}
          sx={{
            ...cellSx(c.width),
            ...(c.key === 'name' ? { p: '8px 12px' } : null),
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            whiteSpace: 'nowrap',
            ...HEADER_TYPO,
          }}
        >
          {c.label}
          {c.key === 'name' && <KeyboardArrowDownIcon sx={{ fontSize: 24 }} />}
        </Box>
      ))}
    </Box>
  );
}

function SkeletonPill({ width = '70%' }: { width?: string | number }) {
  return (
    <Box
      sx={{
        height: 16,
        width,
        bgcolor: '#E3E3E8',
        borderRadius: 999,
      }}
    />
  );
}

function SkeletonRow() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {COLUMNS.map((c) => (
        <Box key={c.key} sx={cellSx(c.width)}>
          <SkeletonPill width={`${60 + ((c.label.length * 7) % 30)}%`} />
        </Box>
      ))}
    </Box>
  );
}

function DataRow({ row, onOpen }: { row: PlanRow; onOpen: () => void }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (shouldActivateRow(e)) {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.12s ease',
        '&:hover': { bgcolor: 'rgba(31, 31, 112, 0.04)' },
        '&:focus-visible': { outline: '2px solid #1F1F70', outlineOffset: -2 },
      }}
    >
      {COLUMNS.map((c) => (
        <Box
          key={c.key}
          title={row[c.key]}
          sx={{
            ...cellSx(c.width),
            ...CELL_TYPO,
            ...(c.key === 'generatedOn' ? { textAlign: 'right' } : null),
          }}
        >
          {row[c.key]}
        </Box>
      ))}
    </Box>
  );
}

const FOOTER_TYPO = {
  fontFamily: PLANS.font,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: PLANS.muted,
} as const;

function PaginationFooter({
  page,
  totalPages,
  totalRows,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  totalRows: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const start = totalRows === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const end = Math.min(totalRows, page * ROWS_PER_PAGE);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '26px',
        py: '2px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={FOOTER_TYPO}>Rows per page:</Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              fontSize: 12,
              lineHeight: '20px',
              letterSpacing: '0.0333em',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            {ROWS_PER_PAGE}
          </Box>
          <ArrowDropDownIcon sx={{ fontSize: 24, color: PLANS.muted }} />
        </Box>
      </Box>
      <Box sx={FOOTER_TYPO}>{`${start}-${end} of ${totalRows}`}</Box>
      <Box sx={{ display: 'flex' }}>
        <IconButton
          aria-label="Previous page"
          onClick={onPrev}
          disabled={page <= 1}
          sx={{ width: 40, height: 40, borderRadius: '8px', p: 1, color: PLANS.ink }}
        >
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <IconButton
          aria-label="Next page"
          onClick={onNext}
          disabled={page >= totalPages}
          sx={{ width: 40, height: 40, borderRadius: '8px', p: 1, color: PLANS.ink }}
        >
          <ChevronRightIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export function PlansPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const location = useLocation();
  const state = location.state as LocationState;
  const count = state?.count ?? 0;
  const skipLoad = state?.skipLoad ?? false;

  const [loading, setLoading] = useState(shouldSimulateLoad(count, skipLoad));
  // Stamped with the generation it answers for, so a roster from an earlier
  // generation is recognised as not being an answer to this one.
  const [serverRoster, setServerRoster] = useState<{
    stamp: string | null;
    rows: PlanRow[];
    failed: boolean;
  } | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const status = usePlansStatus();
  const statusPending = status === null;

  useEffect(() => {
    if (!shouldSimulateLoad(count, skipLoad)) return;
    const t = window.setTimeout(() => setLoading(false), SIMULATED_LOAD_MS);
    return () => window.clearTimeout(t);
  }, [count, skipLoad]);

  const generatedAtIso = status?.generatedAt ?? null;
  const lastUpdated = generatedAtIso ? formatLastUpdated(generatedAtIso) : '';

  const generated = status?.generated === true;

  // The server assembles the same uploads the report was generated from, so
  // these rows and the report cannot disagree about who is on the roster.
  useEffect(() => {
    let cancelled = false;
    // generatedOn is derived inside the callback from generatedAtIso so that
    // the effect dep is the full ISO string: generatedOn is day-granular
    // (MM/DD/YY), so two generations on the same day would share the same
    // formatted string and the second would not trigger a re-fetch.
    const stamp = formatGeneratedOn(generatedAtIso);
    fetchRosterClassification()
      .then((students) =>
        cancelled
          ? null
          : { rows: classificationPlanRows(students ?? [], stamp), failed: students === null },
      )
      // The page renders nothing until this resolves, so a rejection here
      // would leave it loading for ever rather than saying it has nothing.
      // fetchRosterClassification answers null rather than throwing, but the
      // projection runs inside this chain and a throw there must not strand
      // the page.
      .catch(() => ({ rows: [] as PlanRow[], failed: true }))
      .then((answer) => {
        if (!cancelled && answer !== null) {
          setServerRoster({ stamp: generatedAtIso, rows: answer.rows, failed: answer.failed });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [generatedAtIso]);

  // A regeneration replaces who is on the roster, so the previous answer is not
  // a lesser version of the new one, it is a different one. Until this
  // generation's request resolves the page has no answer, rather than an old
  // one — which is the stale reading it was cut over to stop.
  const answered = serverRoster !== null && serverRoster.stamp === generatedAtIso
    ? serverRoster.rows
    : null;

  const rows = useMemo(() => {
    if (!generated || answered === null) return [];
    return [...answered].sort((a, b) => a.name.localeCompare(b.name));
  }, [generated, answered]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.school.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.localId.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  // A regeneration can return fewer students than the page the reader is on,
  // and a page past the end shows an empty table rather than saying so.
  // Clamped at render rather than reset through an effect, so there is no
  // frame where the reader is on a page that does not exist.
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

  const loadFailed =
    serverRoster !== null && serverRoster.stamp === generatedAtIso && serverRoster.failed;
  const isEmpty = !loading && !statusPending && answered !== null && rows.length === 0;
  const statusUnknown = status?.unknown === true;
  // The status answers quickly; the roster can take a minute. Either one still
  // outstanding means there is nothing to show yet.
  const waiting = !loading && (statusPending || answered === null);

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: '#F6F6F8' }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="roster" />
      <Box component="main" sx={{ flex: 1, padding: '32px 24px' }}>
        {isEmpty && (loadFailed || statusUnknown) ? (
          <LoadFailure tokens={PLANS} />
        ) : isEmpty ? (
          <PlansEmptyState
            onGenerate={
              isSuperAdmin
                ? () => navigate((answered?.length ?? 0) > 0 ? '/generate' : '/upload')
                : undefined
            }
          />
        ) : (
        <Paper
          data-testid="plans-content"
          elevation={1}
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            mx: 'auto',
            pt: 3,
            borderRadius: '16px',
            boxShadow: PLANS.cardShadow,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Box
            sx={{
              px: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: PLANS.font,
                  fontSize: 28,
                  fontWeight: 600,
                  lineHeight: '36px',
                  color: PLANS.ink,
                }}
              >
                {loading ? 'Generating Student Action Plans' : 'Student Action Plans'}
              </Typography>
              {(loading || lastUpdated !== '') && (
                <Typography
                  sx={{
                    fontFamily: PLANS.font,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '16px',
                    color: PLANS.muted,
                  }}
                >
                  {loading
                    ? 'Sit tight — we are building each student’s plan from the uploaded data.'
                    : `Last updated ${lastUpdated}`}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                size="small"
                placeholder="Search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
                InputProps={{
                  style: {
                    borderRadius: 8,
                    height: 36,
                    backgroundColor: '#FFFFFF',
                    fontFamily: PLANS.font,
                    fontSize: 16,
                    lineHeight: '20px',
                    color: PLANS.muted,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: PLANS.muted, fontSize: 24 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 264,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: PLANS.border,
                  },
                }}
              />
            </Box>
          </Box>

          {waiting ? (
            <TableLoading tokens={PLANS} rows={ROWS_PER_PAGE} />
          ) : (
          <Box>
          <HeaderRow />

          {loading
            ? Array.from({ length: ROWS_PER_PAGE }).map((_, i) => <SkeletonRow key={i} />)
            : visible.map((row) => (
                <DataRow
                  key={row.id}
                  row={row}
                  onOpen={() => navigate(planPath(row.id), { state: { count, order: filtered.map((r) => r.id) } })}
                />
              ))}

          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            totalRows={filtered.length}
            onPrev={() => setPage(Math.max(1, currentPage - 1))}
            onNext={() => setPage(Math.min(totalPages, currentPage + 1))}
          />
          </Box>
          )}
        </Paper>
        )}
      </Box>
    </Box>
  );
}
